import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  applyConsentToTags,
  getConsentCategories,
  hasConsentDecision,
  persistConsent
} from '../utils/consent';
import { COOKIES_VERSION, VENDORS } from '../data/legalEntity';
import '../styles/CookieConsentBanner.scss';

// First-visit prompt is required for Ads/GA4: defaults stay denied until Accept.
// Footer / "Manage cookies" still opens the banner via aipsyhelp:open-cookie-preferences.
const AUTO_SHOW_BANNER_ON_FIRST_VISIT = true;
const FIRST_VISIT_BANNER_DELAY_MS = 2500;

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const { language = 'en' } = useParams();
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] = useState(getConsentCategories());
  const dialogRef = useRef(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    if (hasConsentDecision()) {
      applyConsentToTags();
      return undefined;
    }
    if (!AUTO_SHOW_BANNER_ON_FIRST_VISIT) {
      return undefined;
    }
    const timer = window.setTimeout(() => setVisible(true), FIRST_VISIT_BANNER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      setCategories(getConsentCategories());
      setShowSettings(true);
      setVisible(true);
    };
    window.addEventListener('aipsyhelp:open-cookie-preferences', openPreferences);
    return () => window.removeEventListener('aipsyhelp:open-cookie-preferences', openPreferences);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const previouslyFocused = document.activeElement;
    const node = dialogRef.current;
    const focusable = () =>
      node
        ? Array.from(
            node.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
          ).filter((el) => !el.hasAttribute('disabled'))
        : [];

    focusable()[0]?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (hasConsentDecision()) {
          setVisible(false);
        }
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [visible, showSettings]);

  const save = (next, source) => {
    persistConsent(next, source);
    setCategories(next);
    setVisible(false);
    setShowSettings(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-consent-banner" role="presentation">
      <div
        className="cookie-consent-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        ref={dialogRef}
      >
        {!showSettings ? (
          <>
            <div className="cookie-text">
              <h2 id={titleId}>{t('cookies.title', 'We value your privacy')}</h2>
              <p id={descId}>
                {t(
                  'cookies.description',
                  'Necessary cookies keep the site working. Analytics and advertising cookies load only after you choose them. Reject is equal to Accept: both choices are recorded.'
                )}
              </p>
              <p className="cookie-privacy-link">
                <Link to={`/${language}/cookies`}>{t('cookies.cookie_notice', 'Cookie notice')}</Link>
                {' · '}
                <Link to={`/${language}/privacy`}>{t('cookies.privacy_policy', 'Privacy Policy')}</Link>
                {' · '}
                <Link to={`/${language}/terms`}>{t('footer.terms', 'Terms of Use')}</Link>
              </p>
            </div>
            <div className="cookie-buttons">
              <button type="button" className="btn-reject" onClick={() => save({ analytics: false, advertising: false }, 'reject')}>
                {t('cookies.reject', 'Reject non-essential')}
              </button>
              <button type="button" className="btn-customize" onClick={() => setShowSettings(true)}>
                {t('cookies.customize', 'Customize')}
              </button>
              <button type="button" className="btn-accept" onClick={() => save({ analytics: true, advertising: true }, 'accept_all')}>
                {t('cookies.accept', 'Accept all')}
              </button>
            </div>
          </>
        ) : (
          <form
            className="cookie-settings"
            onSubmit={(event) => {
              event.preventDefault();
              save(categories, 'customize');
            }}
          >
            <h2 id={titleId}>{t('cookies.settings_title', 'Cookie settings')}</h2>
            <p id={descId}>
              {t('cookies.settings_description', 'Choose which optional cookies we may use. Version')} {COOKIES_VERSION}.
            </p>
            <fieldset className="cookie-settings-options">
              <legend className="visually-hidden">{t('cookies.categories', 'Cookie categories')}</legend>
              <div className="cookie-setting-item">
                <div className="setting-header">
                  <h3>{t('cookies.essential_title', 'Essential')}</h3>
                  <input type="checkbox" checked disabled aria-label={t('cookies.essential_title', 'Essential')} />
                </div>
                <p>{t('cookies.essential_description', 'Required for security, language, consent storage and form delivery.')}</p>
              </div>
              <div className="cookie-setting-item">
                <div className="setting-header">
                  <h3>
                    <label htmlFor="consent-analytics">{t('cookies.analytics_title', 'Analytics')}</label>
                  </h3>
                  <input
                    id="consent-analytics"
                    type="checkbox"
                    role="switch"
                    aria-checked={categories.analytics}
                    checked={categories.analytics}
                    onChange={() => setCategories((prev) => ({ ...prev, analytics: !prev.analytics }))}
                  />
                </div>
                <p>
                  {t('cookies.analytics_description', 'Google Analytics 4 and Microsoft Clarity, only after this switch is on.')}{' '}
                  {VENDORS.filter((vendor) => vendor.category === 'analytics').map((vendor) => vendor.name).join(', ')}.
                </p>
              </div>
              <div className="cookie-setting-item">
                <div className="setting-header">
                  <h3>
                    <label htmlFor="consent-ads">{t('cookies.advertising_title', 'Advertising')}</label>
                  </h3>
                  <input
                    id="consent-ads"
                    type="checkbox"
                    role="switch"
                    aria-checked={categories.advertising}
                    checked={categories.advertising}
                    onChange={() => setCategories((prev) => ({ ...prev, advertising: !prev.advertising }))}
                  />
                </div>
                <p>
                  {t('cookies.advertising_description', 'Google Ads conversion tags, only after this switch is on.')}
                </p>
              </div>
            </fieldset>
            <div className="cookie-settings-buttons">
              <button type="button" className="btn-reject" onClick={() => save({ analytics: false, advertising: false }, 'reject')}>
                {t('cookies.reject_all', 'Reject all optional')}
              </button>
              <button type="submit" className="btn-accept">
                {t('cookies.save_settings', 'Save settings')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
